import React from 'react';
import { BlockComponent } from '@/editor/lib/declarations/EditorComponentTypes';
import { useCurrentBlock } from '@/editor/PageEditor/CurrentBlockProvider';
import Container from '@/components/Container';
import TextInput from '@/components/forms/TextInput';
import SwitchInput from '@/components/forms/SwitchInput';
import DateTimeInput from '@/components/forms/DateTimeInput';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import Styles from '@/assets/js/Styles';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';

export const DatelineBlock: BlockComponent = () => {
  const { blockPath } = useCurrentBlock();
  const { t: tComponents } = useTranslation('components');
  const showLastModified = useWatch({ name: `${blockPath}.showLastModified` }) as boolean | undefined;

  // In 1.0, you could change between subtypes.
  // Here we are just simulating the selection, it will always be dateline.
  const [selectedSubType, setSelectedSubType] = React.useState<'dateline' | 'metadata'>('dateline');

  return (
    <>
      <Container fullWidth left top>
        <RadioGroup
          sx={{
            display: 'flex',
            flexFlow: 'row wrap',
          }}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={(e) => setSelectedSubType(e.target.value as any)}
          value={selectedSubType}>
          <FormControlLabel value='dateline' control={<Radio />} label={tComponents('DatelineBlock.Dateline')} />
          <FormControlLabel value='metadata' control={<Radio />} label={tComponents('DatelineBlock.Metadata')} />
        </RadioGroup>
      </Container>
      {selectedSubType === 'dateline' && (
        <Container fullWidth column left gap={2}>
          <Typography sx={{ textAlign: 'left' }}>{tComponents('DatelineBlock.DatelineDescription')}</Typography>
          {/* One row per date: the text before it, and the date or the switch that turns it on */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, width: '100%', alignItems: 'center' }}>
            <TextInput path={`${blockPath}.text`} label={tComponents('DatelineBlock.LabelText')} defaultValue='' />
            <DateTimeInput
              isLoading={false}
              path='published_at'
              label={tComponents('DatelineBlock.LabelPublishedDate')}
              fullWidth
            />
            <TextInput
              path={`${blockPath}.lastModifiedText`}
              label={tComponents('DatelineBlock.LabelLastModifiedText')}
              placeholder={tComponents('DatelineBlock.LastModifiedDefaultText')}
              defaultValue=''
              disabled={!showLastModified}
            />
            <Box>
              <SwitchInput
                path={`${blockPath}.showLastModified`}
                label={tComponents('DatelineBlock.LabelShowLastModified')}
                fullWidth={false}
              />
              <Typography variant='caption' color='textSecondary' component='p' sx={{ textAlign: 'left' }}>
                {tComponents('DatelineBlock.ShowLastModifiedHelp')}
              </Typography>
            </Box>
          </Box>
          <SwitchInput
            path={`${blockPath}.showTime`}
            label={tComponents('DatelineBlock.LabelShowTime')}
            fullWidth={false}
          />
        </Container>
      )}
      {selectedSubType === 'metadata' && (
        <Container
          fullWidth
          left
          top
          sx={{
            backgroundColor: Styles.Colors.LIGHTEST_BLUE,
            p: 2,
          }}>
          <Typography>{tComponents('DatelineBlock.MetadataDescription')}</Typography>
        </Container>
      )}
    </>
  );
};
